import { FC, PropsWithChildren, useCallback } from 'react';
import { useCreateAiAgentChat } from '@/entities/ai-agent-chat';
import { useSettings } from '@/entities/settings';
import { CopilotContext } from '../model/context';
import { useCmdJ } from '../model/useCmdJ';

export const CopilotProvider: FC<PropsWithChildren> = ({ children }) => {
  const { sidebar, setSidebarSettings } = useSettings();
  const view = sidebar.copilotView ?? 'chat';
  const activeChatId = sidebar.copilotActiveChatId ?? null;
  const activePanel = sidebar.activePanel ?? 'navigation';

  const { mutateAsync: createChatApi, isPending: isCreatingChat } = useCreateAiAgentChat();

  const showChatList = useCallback(() => {
    setSidebarSettings({ copilotView: 'list', activePanel: 'copilot' });
  }, [setSidebarSettings]);

  const openChat = useCallback(
    (chatId: number) => {
      setSidebarSettings({ copilotActiveChatId: chatId, copilotView: 'chat' });
    },
    [setSidebarSettings],
  );

  const createNewChat = useCallback(async () => {
    const chat = await createChatApi();
    setSidebarSettings({ copilotActiveChatId: chat.id, copilotView: 'chat', activePanel: 'copilot' });
  }, [createChatApi, setSidebarSettings]);

  const togglePanel = useCallback(() => {
    setSidebarSettings({ activePanel: activePanel === 'copilot' ? 'navigation' : 'copilot' });
  }, [activePanel, setSidebarSettings]);

  const { shortcut } = useCmdJ(togglePanel);

  return (
    <CopilotContext.Provider
      value={{
        view,
        activeChatId,
        isCreatingChat,
        shortcut,
        showChatList,
        openChat,
        createNewChat,
      }}
    >
      {children}
    </CopilotContext.Provider>
  );
};
